import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtUSD, fmtNum, relTime } from "@/lib/format";
import PriceChart from "@/components/PriceChart";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Star, X } from "lucide-react";

export default function Trade() {
  const [assets, setAssets] = useState([]);
  const [params, setParams] = useSearchParams();
  const initSym = params.get("symbol") || "BTC";
  const initType = params.get("type") || "crypto";
  const [symbol, setSymbol] = useState(initSym);
  const [assetType, setAssetType] = useState(initType);
  const [contracts, setContracts] = useState([]);
  const [side, setSide] = useState("buy");
  const [orderType, setOrderType] = useState("market"); // market | limit | stop | take_profit
  const [route, setRoute] = useState("paper"); // paper | alpaca | ibkr
  const [providers, setProviders] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [triggerPrice, setTriggerPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [bracketSL, setBracketSL] = useState("");
  const [bracketTP, setBracketTP] = useState("");
  const [trailPct, setTrailPct] = useState("");
  const [templates, setTemplates] = useState([]);

  const loadTemplates = () =>
    api.get("/bracket-templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
  useEffect(() => {
    loadTemplates();
  }, []);
  const [quote, setQuote] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [grading, setGrading] = useState(null);

  useEffect(() => {
    api.get("/market/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/brokers").then(({ data }) => setProviders(data)).catch(() => {});
    api.get("/futures/contracts").then(({ data }) => setContracts(data)).catch(() => {});
  }, []);

  const loadQuote = () => {
    api.get("/market/quote", { params: { symbol, asset_type: assetType } })
      .then(({ data }) => setQuote(data))
      .catch(() => setQuote(null));
  };
  const loadPortfolio = () => {
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
  };
  const loadTrades = () => {
    api.get("/trades", { params: { limit: 20 } }).then(({ data }) => setTrades(data)).catch(() => {});
  };
  const loadOrders = async () => {
    // Evaluate then fetch
    await api.post("/orders/check").catch(() => {});
    const { data } = await api.get("/orders", { params: { status: "open" } });
    setOpenOrders(data);
  };

  useEffect(() => {
    loadQuote();
    const t = setInterval(loadQuote, 15000);
    return () => clearInterval(t);
  }, [symbol, assetType]);

  useEffect(() => {
    loadPortfolio();
    loadTrades();
    loadOrders();
  }, []);

  const currentPosition = useMemo(() => {
    if (!portfolio?.positions) return null;
    return portfolio.positions.find((p) => p.symbol === symbol && p.asset_type === assetType);
  }, [portfolio, symbol, assetType]);

  const isFuture = assetType === "future";
  const spec = useMemo(
    () => (isFuture ? contracts.find((c) => c.symbol === symbol) : null),
    [isFuture, contracts, symbol]
  );

  const effectivePrice = orderType === "market"
    ? quote?.price
    : (Number(triggerPrice) || quote?.price);
  const total = effectivePrice && quantity
    ? Number(quantity) * effectivePrice * (isFuture ? spec?.multiplier || 1 : 1)
    : 0;
  const closingQty =
    isFuture && currentPosition &&
    ((side === "sell" && currentPosition.direction === "long") ||
      (side === "buy" && currentPosition.direction === "short"))
      ? currentPosition.quantity
      : 0;
  const marginNeeded = isFuture && spec
    ? Math.max(0, Number(quantity || 0) - closingQty) * spec.initial_margin
    : 0;
  const buyingPower = portfolio ? portfolio.buying_power ?? portfolio.cash_balance : 0;
  const canAfford = portfolio
    ? isFuture
      ? marginNeeded <= buyingPower + 1e-6
      : side === "buy"
      ? portfolio.cash_balance >= total
      : true
    : true;
  const canSell =
    side === "sell" && orderType === "market" && !isFuture
      ? currentPosition && currentPosition.quantity >= Number(quantity || 0)
      : true;

  const showBracket = orderType === "market" && route === "paper" && (side === "buy" || isFuture);
  const tickRound = (v) => {
    if (isFuture && spec?.tick_size) return Math.round((Math.round(v / spec.tick_size) * spec.tick_size) * 100) / 100;
    return v > 10 ? Math.round(v * 100) / 100 : Math.round(v * 10000) / 10000;
  };
  const bracketMarkers = showBracket
    ? [
        ...(bracketSL ? [{ id: "sl", price: Number(bracketSL), label: "STOP", colorClass: "border-loss", badgeClass: "bg-loss text-white" }] : []),
        ...(bracketTP ? [{ id: "tp", price: Number(bracketTP), label: "TARGET", colorClass: "border-gain", badgeClass: "bg-gain text-white" }] : []),
      ]
    : [];
  const trailMarkers = openOrders
    .filter((o) => o.order_type === "trailing_stop" && o.status === "open" && o.symbol === symbol && o.asset_type === assetType)
    .map((o) => ({
      id: `trail-${o._id}`,
      price: o.trigger_price,
      label: `TRAIL ${o.side.toUpperCase()}`,
      colorClass: "border-amber-500",
      badgeClass: "bg-amber-500 text-white",
      draggable: false,
    }));
  const chartMarkers = [...bracketMarkers, ...trailMarkers];
  const onBracketMarkerChange = (id, price) => {
    const v = tickRound(price);
    if (id === "sl") setBracketSL(String(v));
    else setBracketTP(String(v));
  };
  const seedBracket = () => {
    if (!quote?.price) return;
    const shortEntry = side === "sell" && isFuture;
    setBracketSL(String(tickRound(quote.price * (shortEntry ? 1.02 : 0.98))));
    setBracketTP(String(tickRound(quote.price * (shortEntry ? 0.98 : 1.02))));
  };
  const applyTemplate = (t) => {
    if (!quote?.price) return;
    const shortEntry = side === "sell" && isFuture;
    const slMult = shortEntry ? 1 + t.stop_pct / 100 : 1 - t.stop_pct / 100;
    const tpMult = shortEntry ? 1 - t.target_pct / 100 : 1 + t.target_pct / 100;
    setBracketSL(String(tickRound(quote.price * slMult)));
    setBracketTP(String(tickRound(quote.price * tpMult)));
  };
  const saveTemplate = async () => {
    if (!quote?.price || !bracketSL || !bracketTP) {
      toast.error("Set both stop and target first");
      return;
    }
    const stop_pct = Math.round(Math.abs(1 - Number(bracketSL) / quote.price) * 10000) / 100;
    const target_pct = Math.round(Math.abs(Number(bracketTP) / quote.price - 1) * 10000) / 100;
    try {
      const { data } = await api.post("/bracket-templates", { stop_pct, target_pct });
      setTemplates((ts) => [...ts, data]);
      toast.success(`Saved template ${data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save template");
    }
  };
  const deleteTemplate = async (t) => {
    try {
      await api.delete(`/bracket-templates/${t._id}`);
      setTemplates((ts) => ts.filter((x) => x._id !== t._id));
    } catch {
      toast.error("Could not delete template");
    }
  };
  const toggleDefaultTemplate = async (t) => {
    try {
      const { data } = await api.post(`/bracket-templates/${t._id}/default`);
      setTemplates((ts) => ts.map((x) => ({ ...x, is_default: x._id === data.default_id })));
      toast.success(data.default_id ? `${t.name} is now your default bracket` : "Default bracket cleared");
    } catch {
      toast.error("Could not update default");
    }
  };

  // Auto-apply the default template once per symbol/side so no trade goes unprotected.
  const autoAppliedRef = useRef("");
  useEffect(() => {
    const def = templates.find((t) => t.is_default);
    if (!def || !showBracket || !quote?.price) return;
    const key = `${symbol}:${side}`;
    if (autoAppliedRef.current === key) return;
    autoAppliedRef.current = key;
    applyTemplate(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, symbol, side, showBracket, quote?.price]);

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!quantity || Number(quantity) <= 0) return;
    setBusy(true);
    try {
      // --- Alpaca route ---
      if (route === "alpaca") {
        if (assetType !== "stock") {
          toast.error("Alpaca route currently supports stocks only.");
          setBusy(false);
          return;
        }
        if (orderType !== "market" && (!triggerPrice || Number(triggerPrice) <= 0)) {
          toast.error(`Enter a ${orderType} price`);
          setBusy(false);
          return;
        }
        const payload = {
          symbol,
          side,
          type: orderType,
          time_in_force: "day",
          qty: Number(quantity),
        };
        if (orderType === "limit") payload.limit_price = Number(triggerPrice);
        if (orderType === "stop") payload.stop_price = Number(triggerPrice);
        const { data } = await api.post("/broker/orders", payload);
        toast.success(`Alpaca order ${data.id?.slice(0, 8) || ""} submitted`);
        setQuantity("");
        setTriggerPrice("");
        setNotes("");
        setBusy(false);
        return;
      }

      // --- IBKR route (stub, always disabled) ---
      if (route === "ibkr") {
        toast.error("IBKR live orders are disabled in the MVP. Save your config on the Broker page.");
        setBusy(false);
        return;
      }

      // --- Paper (default) route ---
      if (orderType === "market") {
        const body = {
          symbol,
          asset_type: assetType,
          side,
          quantity: Number(quantity),
          notes,
        };
        if (bracketSL || bracketTP) {
          body.bracket = {};
          if (bracketSL) body.bracket.stop_loss = Number(bracketSL);
          if (bracketTP) body.bracket.take_profit = Number(bracketTP);
        }
        const { data } = await api.post("/trades", body);
        const nb = (data.bracket_orders || []).length;
        toast.success(
          `${side.toUpperCase()} ${fmtNum(Number(quantity))} ${symbol} @ ${fmtUSD(quote.price)}` +
            (nb ? ` + ${nb} bracket exit${nb > 1 ? "s" : ""} attached` : "")
        );
        setQuantity("");
        setNotes("");
        setBracketSL("");
        setBracketTP("");
        loadPortfolio();
        loadTrades();
        if (nb) loadOrders();
        // Auto-grade
        setGrading({ id: data._id, grade: null, reasoning: null, loading: true, trade: data });
        try {
          const { data: g } = await api.post(`/trades/${data._id}/grade`);
          setGrading({ id: data._id, grade: g.grade, reasoning: g.reasoning, trade: data });
          toast.success(`Trade graded ${g.grade}`);
          loadTrades();
        } catch (err) {
          setGrading((s) => (s ? { ...s, loading: false, reasoning: "AI grading failed." } : s));
        }
      } else if (orderType === "trailing_stop") {
        if (!trailPct || Number(trailPct) <= 0) {
          toast.error("Enter a trail percentage");
          setBusy(false);
          return;
        }
        const { data } = await api.post("/orders", {
          symbol,
          asset_type: assetType,
          side,
          order_type: "trailing_stop",
          quantity: Number(quantity),
          trail_pct: Number(trailPct),
          notes,
        });
        toast.success(
          `TRAILING-STOP ${side} placed (${trailPct}% trail, stop starts at ${fmtUSD(data.trigger_price)})`
        );
        setQuantity("");
        setTrailPct("");
        setNotes("");
        loadOrders();
      } else {
        if (!triggerPrice || Number(triggerPrice) <= 0) {
          toast.error("Enter a trigger price");
          setBusy(false);
          return;
        }
        await api.post("/orders", {
          symbol,
          asset_type: assetType,
          side,
          order_type: orderType,
          quantity: Number(quantity),
          trigger_price: Number(triggerPrice),
          notes,
        });
        toast.success(`${orderType.replace("_", "-").toUpperCase()} ${side} order placed at ${fmtUSD(Number(triggerPrice))}`);
        setQuantity("");
        setTriggerPrice("");
        setNotes("");
        loadOrders();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed");
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async (id) => {
    await api.delete(`/orders/${id}`).catch(() => {});
    toast.success("Order cancelled");
    loadOrders();
  };

  const addToWatchlist = async () => {
    try {
      await api.post("/watchlist", { symbol, asset_type: assetType });
      toast.success(`${symbol} added to watchlist`);
    } catch {
      toast.error("Could not add");
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="trade-root">
      {/* Left: chart + info + orders */}
      <div className="lg:col-span-8 space-y-6">
        <Card className="border border-gray-200 rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Select
                value={`${assetType}:${symbol}`}
                onValueChange={(v) => {
                  const [t, s] = v.split(":");
                  setAssetType(t);
                  setSymbol(s);
                  setParams({ symbol: s, type: t });
                }}
              >
                <SelectTrigger data-testid="asset-selector" className="w-56 h-11 rounded-xl border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {["crypto", "stock", "future"].map((cat) => (
                    <div key={cat}>
                      <p className="px-2 py-1.5 text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">
                        {cat === "future" ? "futures" : cat}
                      </p>
                      {assets
                        .filter((a) => a.asset_type === cat)
                        .map((a) => (
                          <SelectItem key={`${a.asset_type}:${a.symbol}`} value={`${a.asset_type}:${a.symbol}`}>
                            <span className="font-medium mr-2">{a.symbol}</span>
                            <span className="text-gray-500 text-xs">{a.name}</span>
                          </SelectItem>
                        ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={addToWatchlist}
                data-testid="add-to-watchlist"
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-brand-deep"
                title="Add to watchlist"
              >
                <Star size={16} />
              </button>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">Price</p>
              <p data-testid="current-price" className="font-display text-3xl font-medium tabular-nums">
                {quote ? fmtUSD(quote.price, { dp: quote.price > 10 ? 2 : 4 }) : "—"}
              </p>
            </div>
          </div>
          <div className="mt-6">
            {isFuture && spec && (
              <div
                className="mb-4 grid grid-cols-2 sm:grid-cols-5 gap-3 rounded-xl bg-gray-50 border border-gray-100 p-4"
                data-testid="contract-spec-card"
              >
                <SpecCell label="Tick size" value={spec.tick_size} />
                <SpecCell label="Tick value" value={fmtUSD(spec.tick_value)} />
                <SpecCell label="Multiplier" value={`${spec.multiplier}×`} />
                <SpecCell label="Margin / contract" value={fmtUSD(spec.initial_margin)} />
                <SpecCell label="Exchange" value={spec.exchange} />
                <p className="col-span-2 sm:col-span-5 text-[10px] text-gray-400" data-testid="contract-expiry-line">
                  {spec.session} · {spec.expiration_mode} front-month
                  {spec.expires_on && (
                    <span className={spec.days_to_expiration <= 7 ? " text-amber-600 font-semibold" : ""}>
                      {" "}· expires {spec.expires_on} ({spec.days_to_expiration}d)
                    </span>
                  )}
                </p>
              </div>
            )}
            <Tabs defaultValue="7">
              <TabsList className="rounded-full bg-gray-100 p-1">
                <TabsTrigger value="1" data-testid="range-1d" className="rounded-full text-xs">1D</TabsTrigger>
                <TabsTrigger value="7" data-testid="range-7d" className="rounded-full text-xs">7D</TabsTrigger>
                <TabsTrigger value="30" data-testid="range-30d" className="rounded-full text-xs">30D</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-4">
              <PriceChart
                symbol={symbol}
                assetType={assetType}
                days={7}
                height={320}
                markers={chartMarkers}
                onMarkerChange={onBracketMarkerChange}
              />
            </div>
          </div>
        </Card>

        {grading && (
          <Card className="border border-gray-200 rounded-2xl p-6" data-testid="grading-card">
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center font-display text-3xl font-bold ${
                  !grading.grade
                    ? "bg-gray-100 text-gray-400"
                    : ["A", "B"].includes(grading.grade)
                    ? "bg-gain-soft text-gain"
                    : grading.grade === "C"
                    ? "bg-gray-100 text-gray-700"
                    : "bg-loss-soft text-loss"
                }`}
                data-testid="grade-letter"
              >
                {grading.grade || "…"}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 flex items-center gap-1">
                  <Sparkles size={12} /> Claude reasoning
                </p>
                <p className="mt-2 text-gray-800 leading-relaxed" data-testid="grade-reasoning">
                  {grading.reasoning || "Analyzing your trade…"}
                </p>
              </div>
            </div>
          </Card>
        )}

        {openOrders.length > 0 && (
          <Card className="border border-gray-200 rounded-2xl p-6" data-testid="open-orders-card">
            <p className="font-display font-semibold text-lg mb-4">Open orders</p>
            <div className="divide-y divide-gray-100">
              {openOrders.map((o) => (
                <div key={o._id} className="flex items-center justify-between py-3" data-testid={`order-row-${o._id}`}>
                  <div>
                    <p className="text-sm font-medium">
                      <span className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 mr-2">
                        {o.order_type}
                      </span>
                      <span className={o.side === "buy" ? "text-gain" : "text-loss"}>{o.side.toUpperCase()}</span>{" "}
                      {fmtNum(o.quantity)} {o.symbol}
                    </p>
                    <p className="text-xs text-gray-500">
                      Trigger: <span className="font-mono">{fmtUSD(o.trigger_price)}</span> · {relTime(o.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => cancelOrder(o._id)}
                    data-testid={`cancel-order-${o._id}`}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-loss"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="trade-history-card">
          <p className="font-display font-semibold text-lg mb-4">Your trade history</p>
          {trades.length === 0 ? (
            <p className="text-sm text-gray-500">No trades yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {trades.map((t) => (
                <div key={t._id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium">
                      <span className={t.side === "buy" ? "text-gain" : "text-loss"}>{t.side.toUpperCase()}</span>{" "}
                      {fmtNum(t.quantity)} {t.symbol}
                    </p>
                    <p className="text-xs text-gray-500">
                      @ {fmtUSD(t.price, { dp: t.price > 10 ? 2 : 4 })} · {relTime(t.created_at)}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <p className="text-sm font-mono">{fmtUSD(t.total)}</p>
                    {t.grade && (
                      <Badge variant="secondary" className="text-[10px]" data-testid={`trade-grade-${t._id}`}>
                        {t.grade}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Right: Order form */}
      <div className="lg:col-span-4">
        <Card className="border border-gray-200 rounded-2xl p-6 sticky top-24">
          <p className="font-display font-semibold text-lg">Place order</p>

          {/* Route selector */}
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500 mb-2">Route</p>
            <div className="grid grid-cols-3 gap-1 rounded-full bg-gray-100 p-1" data-testid="route-selector">
              {["paper", "alpaca", "ibkr"].map((r) => {
                const meta = providers.find((p) => p.provider === r);
                const isIBKR = r === "ibkr";
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => !isIBKR && setRoute(r)}
                    disabled={isIBKR}
                    data-testid={`route-${r}`}
                    title={
                      r === "paper"
                        ? "Paper trading engine (default)"
                        : r === "alpaca"
                        ? "Route to your connected Alpaca paper account"
                        : "IBKR live orders are disabled in the MVP"
                    }
                    className={`rounded-full text-xs font-medium py-1.5 transition-colors ${
                      route === r
                        ? "bg-white text-black shadow-sm"
                        : isIBKR
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-600"
                    }`}
                  >
                    {r === "paper" ? "Paper" : meta?.label?.split(" ")[0] || r}
                    {isIBKR && <span className="ml-1 text-[9px] uppercase opacity-70">stub</span>}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5" data-testid="route-hint">
              {route === "paper" && "Simulated trade — graded by Claude."}
              {route === "alpaca" && (assetType === "stock"
                ? "Routes to your connected Alpaca paper account."
                : "Alpaca only supports stocks. Switch symbol or route.")}
              {route === "ibkr" && "IBKR live orders disabled — configure gateway on Broker page."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => setSide("buy")}
              data-testid="side-buy"
              className={`rounded-full py-2.5 text-sm font-medium transition-colors ${
                side === "buy" ? "bg-gain text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {isFuture ? "Buy / Long" : "Buy"}
            </button>
            <button
              onClick={() => setSide("sell")}
              data-testid="side-sell"
              className={`rounded-full py-2.5 text-sm font-medium transition-colors ${
                side === "sell" ? "bg-loss text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {isFuture ? "Sell / Short" : "Sell"}
            </button>
          </div>

          <div className="mt-4 grid grid-cols-5 gap-1 rounded-full bg-gray-100 p-1">
            {["market", "limit", "stop", "take_profit", "trailing_stop"].map((k) => (
              <button
                key={k}
                onClick={() => setOrderType(k)}
                data-testid={`order-type-${k}`}
                className={`rounded-full text-xs font-medium py-1.5 transition-colors ${
                  orderType === k ? "bg-white text-black shadow-sm" : "text-gray-600"
                }`}
              >
                {k === "take_profit" ? "TP" : k === "trailing_stop" ? "Trail" : k.charAt(0).toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>

          <form onSubmit={placeOrder} className="mt-5 space-y-4" data-testid="order-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">
                {isFuture ? "Contracts" : "Quantity"}
              </Label>
              <Input
                data-testid="order-quantity"
                type="number"
                step={isFuture ? "1" : "any"}
                min={isFuture ? "1" : "0"}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={isFuture ? "1" : "0.00"}
                className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
              />
              {currentPosition && (
                <p className="text-[11px] text-gray-500 mt-1" data-testid="current-position-hint">
                  You hold: <span className="font-mono">{fmtNum(currentPosition.quantity)}</span> {symbol}
                  {currentPosition.direction && (
                    <span className={`ml-1 font-bold uppercase ${currentPosition.direction === "long" ? "text-gain" : "text-loss"}`}>
                      {currentPosition.direction}
                    </span>
                  )}
                </p>
              )}
            </div>

            {showBracket && (
              <div className="rounded-xl border border-dashed border-gray-200 p-3" data-testid="bracket-section">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-gray-500">
                    Bracket exits <span className="font-normal normal-case text-gray-400">(optional — one cancels the other)</span>
                  </p>
                  <button
                    onClick={seedBracket}
                    data-testid="bracket-chart-seed"
                    className="text-[10px] font-medium text-brand hover:text-brand-deep whitespace-nowrap"
                  >
                    Set on chart ±2%
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {templates.map((t, i) => (
                    <span key={t._id} className={`inline-flex items-center rounded-full transition-colors ${t.is_default ? "bg-brand/10 ring-1 ring-brand/30" : "bg-gray-100 hover:bg-gray-200"}`}>
                      <button
                        onClick={() => toggleDefaultTemplate(t)}
                        data-testid={`bracket-template-default-${i}`}
                        className={`pl-2 py-1 text-[11px] ${t.is_default ? "text-brand" : "text-gray-300 hover:text-brand"}`}
                        aria-label="Set as default"
                        title={t.is_default ? "Default — applies automatically" : "Make default"}
                      >
                        ★
                      </button>
                      <button
                        onClick={() => applyTemplate(t)}
                        data-testid={`bracket-template-chip-${i}`}
                        className="text-[10px] font-medium text-gray-700 pl-1 py-1"
                      >
                        {t.name}
                      </button>
                      <button
                        onClick={() => deleteTemplate(t)}
                        data-testid={`bracket-template-delete-${i}`}
                        className="text-gray-400 hover:text-loss px-1.5 py-1"
                        aria-label="Delete template"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(bracketSL && bracketTP) && (
                    <button
                      onClick={saveTemplate}
                      data-testid="bracket-template-save"
                      className="text-[10px] font-medium text-brand hover:text-brand-deep px-1.5 py-1"
                    >
                      + Save as template
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Input
                      data-testid="bracket-stop-input"
                      type="number"
                      step="any"
                      min="0"
                      value={bracketSL}
                      onChange={(e) => setBracketSL(e.target.value)}
                      placeholder="Stop-loss price"
                      className="h-10 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      data-testid="bracket-tp-input"
                      type="number"
                      step="any"
                      min="0"
                      value={bracketTP}
                      onChange={(e) => setBracketTP(e.target.value)}
                      placeholder="Take-profit price"
                      className="h-10 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand font-mono text-sm"
                    />
                  </div>
                </div>
                {(bracketSL || bracketTP) && effectivePrice && (
                  <p className="text-[10px] text-gray-400 mt-1.5" data-testid="bracket-hint">
                    {side === "buy"
                      ? `Exits will sell: stop below ~${fmtUSD(effectivePrice)}, target above. Drag the lines on the chart to adjust.`
                      : `Exits will buy back: stop above ~${fmtUSD(effectivePrice)}, target below. Drag the lines on the chart to adjust.`}
                  </p>
                )}
              </div>
            )}
            {orderType === "trailing_stop" && (
              <div data-testid="trailing-section">
                <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Trail distance (%)</Label>
                <Input
                  data-testid="trail-pct-input"
                  type="number"
                  step="any"
                  min="0.05"
                  max="50"
                  value={trailPct}
                  onChange={(e) => setTrailPct(e.target.value)}
                  placeholder="e.g. 2"
                  className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {side === "sell"
                    ? `Stop trails ${trailPct || "X"}% below the highest price seen — locks in gains as price rises.`
                    : `Stop trails ${trailPct || "X"}% above the lowest price seen — covers your short as price falls.`}
                  {quote?.price && trailPct
                    ? ` Starts at ${fmtUSD(quote.price * (side === "sell" ? 1 - Number(trailPct) / 100 : 1 + Number(trailPct) / 100))}.`
                    : ""}
                </p>
              </div>
            )}
            {orderType !== "market" && orderType !== "trailing_stop" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">
                  {orderType === "limit" ? "Limit price" : orderType === "stop" ? "Stop price" : "Take-profit price"}
                </Label>
                <Input
                  data-testid="order-trigger-price"
                  type="number"
                  step="any"
                  min="0"
                  value={triggerPrice}
                  onChange={(e) => setTriggerPrice(e.target.value)}
                  placeholder={quote ? String(quote.price) : "0.00"}
                  className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {orderType === "limit"
                    ? side === "buy"
                      ? "Fills when price drops to or below this."
                      : "Fills when price rises to or above this."
                    : orderType === "stop"
                    ? side === "buy"
                      ? "Triggers a buy when price rises to or above this (breakout)."
                      : "Triggers a sell when price falls to or below this (stop loss)."
                    : side === "sell"
                    ? "Take profit: closes your long when price rises to or above this."
                    : isFuture
                    ? "Take profit: covers your short when price falls to or below this."
                    : "Take profit buy: fills when price falls to or below this (buy the dip)."}
                </p>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Notes (optional)</Label>
              <Textarea
                data-testid="order-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why are you entering this trade?"
                className="rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 min-h-[70px]"
              />
            </div>

            <div className="rounded-xl bg-gray-50 p-4 space-y-2 text-sm">
              <Row
                label={
                  orderType === "market"
                    ? "Price"
                    : orderType === "trailing_stop"
                    ? "Current price"
                    : `${orderType === "take_profit" ? "Take-profit" : orderType[0].toUpperCase() + orderType.slice(1)} price`
                }
                value={effectivePrice ? fmtUSD(effectivePrice, { dp: effectivePrice > 10 ? 2 : 4 }) : "—"}
              />
              <Row label={isFuture ? "Notional value" : "Estimated total"} value={fmtUSD(total)} bold />
              {isFuture && spec && (
                <Row
                  label="Margin required"
                  value={fmtUSD(marginNeeded)}
                  cls={!canAfford ? "text-loss" : ""}
                />
              )}
              {portfolio && (
                <Row
                  label={isFuture ? "Buying power" : "Cash available"}
                  value={fmtUSD(isFuture ? buyingPower : portfolio.cash_balance)}
                  cls={!canAfford && (side === "buy" || isFuture) ? "text-loss" : ""}
                />
              )}
            </div>

            <Button
              data-testid="place-order-btn"
              type="submit"
              disabled={
                busy || !quote || !quantity || Number(quantity) <= 0 ||
                (route === "paper" && (!canAfford || !canSell)) ||
                (route === "alpaca" && assetType !== "stock") ||
                route === "ibkr"
              }
              className={`w-full h-12 rounded-full font-medium text-white ${
                side === "buy" ? "bg-gain hover:opacity-90" : "bg-loss hover:opacity-90"
              }`}
            >
              {busy
                ? "Placing…"
                : route === "alpaca"
                ? `${side === "buy" ? "Buy" : "Sell"} ${symbol} on Alpaca`
                : route === "ibkr"
                ? "IBKR disabled"
                : orderType === "market"
                ? `Review & ${side === "buy" ? "Buy" : "Sell"} ${symbol}`
                : `Place ${orderType.replace("_", "-")} ${side}`}
            </Button>
            <p className="text-[11px] text-gray-400 text-center">
              {orderType === "market"
                ? "Market orders are graded by Claude Sonnet 4.5 automatically."
                : "Limit / stop orders fill when their trigger is met."}
            </p>
          </form>

          <Link
            to="/alerts"
            className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-600 hover:text-brand-deep"
            data-testid="link-alerts"
          >
            Set a price alert on {symbol} →
          </Link>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, bold, cls = "" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={`tabular-nums font-mono ${bold ? "font-semibold text-black" : ""} ${cls}`}>{value}</span>
    </div>
  );
}

function SpecCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">{label}</p>
      <p className="font-mono text-sm mt-0.5">{value}</p>
    </div>
  );
}
